import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mic, Volume2, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function buildAudioDeviceList(devs, kind) {
  // Drop entries with no deviceId — Radix <SelectItem /> crashes on value="".
  // Headless browsers and revoked-permission edge cases can return these.
  const filtered = devs.filter(d => d.kind === kind && d.deviceId);
  let unlabeled = 0;
  return filtered.map(d => {
    if (d.label) return { id: d.deviceId, label: d.label };
    unlabeled += 1;
    const base = kind === 'audioinput' ? 'Microphone' : 'Speaker';
    if (d.deviceId === 'default') return { id: d.deviceId, label: `Default ${base.toLowerCase()}` };
    if (d.deviceId === 'communications') return { id: d.deviceId, label: `Communications ${base.toLowerCase()}` };
    return { id: d.deviceId, label: `${base} ${unlabeled}` };
  });
}

async function queryMicPermissionState() {
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'microphone' });
      return status.state;
    }
  } catch { /* Permissions API not available — fall through */ }
  return 'unknown';
}

export default function AudioDeviceBar() {
  const [mics, setMics] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [micId, setMicId] = useState('');
  const [speakerId, setSpeakerId] = useState('');
  const [permission, setPermission] = useState('unknown');
  const [testing, setTesting] = useState(false);
  const testAudioRef = useRef(null);

  const enumerate = useCallback(async (forceGrant = false) => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setPermission('denied');
      return null;
    }
    try {
      const pstate = await queryMicPermissionState();
      let granted = pstate === 'granted';

      let devs = await navigator.mediaDevices.enumerateDevices();
      const audioCount = devs.filter(d => d.kind === 'audioinput' || d.kind === 'audiooutput').length;
      const hasLabels = devs.some(d => (d.kind === 'audioinput' || d.kind === 'audiooutput') && d.label);

      const needsGesture = forceGrant || pstate === 'prompt' || pstate === 'unknown' || (granted && !hasLabels);
      if (needsGesture) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          devs = await navigator.mediaDevices.enumerateDevices();
          stream.getTracks().forEach(t => t.stop());
          granted = true;
        } catch (err) {
          if (err && err.name === 'NotAllowedError') {
            setPermission('denied');
          } else {
            setPermission(pstate === 'denied' ? 'denied' : 'prompt');
          }
        }
      }

      setPermission(granted ? 'granted' : (pstate === 'denied' ? 'denied' : 'prompt'));

      const ins = buildAudioDeviceList(devs, 'audioinput');
      const outs = buildAudioDeviceList(devs, 'audiooutput');
      // eslint-disable-next-line no-console
      console.debug('[AudioDeviceBar] enumerated', { permission: granted ? 'granted' : pstate, audioCount, hasLabels, ins, outs });
      setMics(ins);
      setSpeakers(outs);
      return { ins, outs };
    } catch (e) {
      console.warn('enumerateDevices failed:', e);
      return null;
    }
  }, []);

  const loadSaved = useCallback(async (devs) => {
    try {
      const r = await axios.get(`${API}/api/user/preferences/audio_settings`).catch(() => null);
      const saved = r?.data?.value || {};
      const savedMic = saved.microphone_id || '';
      const savedSpk = saved.speaker_id || '';
      const validMic = devs.ins.some(d => d.id === savedMic);
      const validSpk = devs.outs.some(d => d.id === savedSpk);
      setMicId(validMic ? savedMic : (devs.ins[0]?.id || ''));
      setSpeakerId(validSpk ? savedSpk : (devs.outs[0]?.id || ''));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let permStatus = null;
    const onPermChange = async () => {
      const devs = await enumerate();
      if (cancelled || !devs) return;
      await loadSaved(devs);
    };
    (async () => {
      const devs = await enumerate();
      if (cancelled || !devs) return;
      await loadSaved(devs);
      try {
        if (navigator.permissions?.query) {
          permStatus = await navigator.permissions.query({ name: 'microphone' });
          permStatus.addEventListener?.('change', onPermChange);
        }
      } catch { /* not supported */ }
    })();
    const onDeviceChange = async () => { const devs = await enumerate(); if (devs) await loadSaved(devs); };
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
      permStatus?.removeEventListener?.('change', onPermChange);
    };
  }, [enumerate, loadSaved]);

  const persist = async (nextMic, nextSpeaker) => {
    try {
      const existing = await axios.get(`${API}/api/user/preferences/audio_settings`).catch(() => null);
      const value = {
        ...(existing?.data?.value || {}),
        microphone_id: nextMic,
        speaker_id: nextSpeaker,
        updated_at: new Date().toISOString(),
      };
      await axios.put(`${API}/api/user/preferences/audio_settings`, { value });
      window.dispatchEvent(new CustomEvent('audio-settings-changed', { detail: value }));
    } catch (e) {
      toast.error('Failed to save audio device');
    }
  };

  const onPickMic = (v) => { setMicId(v); persist(v, speakerId); };
  const onPickSpeaker = (v) => { setSpeakerId(v); persist(micId, v); };

  const testSpeaker = async () => {
    if (!speakerId) return;
    setTesting(true);
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.10;
      osc.connect(gain);
      gain.connect(dest);
      osc.start();

      const audio = testAudioRef.current || new Audio();
      testAudioRef.current = audio;
      audio.srcObject = dest.stream;
      if (audio.setSinkId) {
        try { await audio.setSinkId(speakerId); }
        catch (e) { toast.error('Speaker switch not supported on this browser'); }
      }
      await audio.play().catch(() => {});
      setTimeout(() => {
        try { osc.stop(); } catch {}
        try { ctx.close(); } catch {}
        try { audio.pause(); audio.srcObject = null; } catch {}
        setTesting(false);
      }, 700);
    } catch (e) {
      setTesting(false);
    }
  };

  const requestPermission = async () => {
    const devs = await enumerate(true);
    if (devs) {
      await loadSaved(devs);
      if (!devs.ins.length && !devs.outs.length) {
        toast.error('No audio devices detected on this PC');
      }
    } else {
      toast.error('Microphone permission denied — allow it in your browser to pick devices');
    }
  };

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
            <Volume2 className="h-4 w-4 text-slate-400" />
            Audio devices
          </div>

          <div className="flex items-center gap-2 min-w-[260px] flex-1">
            <Mic className="h-4 w-4 text-slate-500 shrink-0" />
            <Select value={micId || ''} onValueChange={onPickMic} disabled={mics.length === 0}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={mics.length ? 'Select microphone…' : 'No microphones'} />
              </SelectTrigger>
              <SelectContent>
                {mics.map(d => (<SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 min-w-[260px] flex-1">
            <Volume2 className="h-4 w-4 text-slate-500 shrink-0" />
            <Select value={speakerId || ''} onValueChange={onPickSpeaker} disabled={speakers.length === 0}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={speakers.length ? 'Select speaker…' : 'No speakers'} />
              </SelectTrigger>
              <SelectContent>
                {speakers.map(d => (<SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={testSpeaker} disabled={!speakerId || testing} className="shrink-0">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Test'}
            </Button>
          </div>

          {permission !== 'granted' ? (
            <Button size="sm" variant="outline" onClick={requestPermission} className="shrink-0">
              Allow mic
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => enumerate(true)}
              title={`Refresh device list (${mics.length} mic${mics.length === 1 ? '' : 's'}, ${speakers.length} speaker${speakers.length === 1 ? '' : 's'})`}
              className="shrink-0"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {permission === 'granted' && mics.length <= 1 && speakers.length <= 1 && (
          <p className="text-[11px] text-amber-600 mt-2">
            Only the system default is listed. If you don't see your other devices, click the refresh icon to re-enumerate.
          </p>
        )}
        {permission === 'denied' && (
          <p className="text-[11px] text-amber-600 mt-2">
            Microphone access is blocked. Click the camera/mic icon in your browser's address bar to allow it, then click "Allow mic".
          </p>
        )}
      </CardContent>
    </Card>
  );
}
